import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component } from '@angular/core';
import { RouterLink } from '@angular/router';
import { ImageModule } from '@df/module-image/image.module';
import { BgColorOptions, HorizontalAlignmentOptions, UndefinedOption } from '@df/ui/atomic/atomic-options';
import { SizeClassDirective } from '@df/ui/common/size-class.directive';
import { VimeoPlayerComponent } from '@df/ui/display/media/vimeo-player.component';
import { YoutubePlayerComponent } from '@df/ui/display/media/youtube-player.component';
import { UiComponent } from '@df/ui/ui.component';
import { ICmsClientModule } from '@icms/client/icms-client.module';
import { ICmsComponent, ICmsContent, NICmsContentComponent, NICmsContentField } from '@icms/core/content';
export interface IPageComponentEditorialLayoutData extends NICmsContentComponent.IData {
    variant: 'image' | 'video';
    image: string;
    video_url: string;
    video_provider: 'youtube' | 'vimeo';
    header?: string;
    title?: string;
    subtitle?: string;
    horizontal_text_align: string;
    cta_link?: string;
    cta_copy?: string;
    background_color?: string;
    swap_position?: string;
}
const isImageLayout = (data: IPageComponentEditorialLayoutData) => data.variant === 'image';
const isVideoLayout = (data: IPageComponentEditorialLayoutData) => data.variant === 'video';
@ICmsComponent<PageComponentEditorialLayoutComponent>({
    id: 'editorial-layout',
    name: 'Editorial Layout',
    tags: ['editorial', 'content'],
    fields: [
        <NICmsContentField.IChoiceField>{
            key: 'background_color',
            type: NICmsContentField.EFieldType.choice,
            colorPreview: true,
            title: 'Background Color',
            options: BgColorOptions,
            description: 'Background color'
        },
        <NICmsContentField.IField>{
            key: 'swap_position',
            type: NICmsContentField.EFieldType.toggle,
            title: 'Swap position video / image',
            default: false
        },
        <NICmsContentField.IChoiceField>{
            key: 'variant',
            type: NICmsContentField.EFieldType.choice,
            title: 'Layout Variant',
            options: [UndefinedOption, { key: 'image', title: 'Image' }, { key: 'video', title: 'Video' }],
            required: true
        },
        <NICmsContentField.IField>{
            key: 'image',
            type: NICmsContentField.EFieldType.image,
            title: 'Image',
            required: true,
            canShow: isImageLayout
        },
        <NICmsContentField.IChoiceField>{
            key: 'video_provider',
            type: NICmsContentField.EFieldType.choice,
            title: 'Provider',
            options: [
                { key: 'youtube', title: 'YouTube' },
                { key: 'vimeo', title: 'Vimeo' }
            ],
            required: true,
            description: 'Choose a video provider',
            canShow: isVideoLayout
        },
        <NICmsContentField.IField>{
            key: 'video_url',
            type: NICmsContentField.EFieldType.text,
            title: 'Video url',
            description: 'Vimeo URL',
            required: true,
            canShow: isVideoLayout
        },
        <NICmsContentField.IChoiceField>{
            key: 'horizontal_text_align',
            type: NICmsContentField.EFieldType.choice,
            title: 'Horizontal text alignment',
            options: HorizontalAlignmentOptions,
            default: 'left'
        },
        <NICmsContentField.IField>{
            key: 'header',
            type: NICmsContentField.EFieldType.text,
            title: 'Header'
        },
        <NICmsContentField.IField>{
            key: 'title',
            type: NICmsContentField.EFieldType.text,
            title: 'Title'
        },
        <NICmsContentField.IField>{
            key: 'subtitle',
            type: NICmsContentField.EFieldType.textarea,
            title: 'Subtitle'
        },
        <NICmsContentField.IField>{
            key: 'cta_copy',
            type: NICmsContentField.EFieldType.text,
            title: 'CTA copy'
        },
        <NICmsContentField.IField>{
            key: 'cta_link',
            type: NICmsContentField.EFieldType.text,
            title: 'CTA link'
        }
    ]
})
@Component({
    selector: 'page-component-editorial-layout',
    templateUrl: 'editorial-layout.html',
    styleUrls: ['editorial-layout.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [CommonModule, SizeClassDirective, RouterLink, YoutubePlayerComponent, VimeoPlayerComponent, ImageModule, ICmsClientModule]
})
export class PageComponentEditorialLayoutComponent extends UiComponent implements NICmsContentComponent.IComponent {
    @ICmsContent({
        type: 'component'
    })
    component!: IPageComponentEditorialLayoutData;
}
